import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { devLog } from '../../utils/devLogger';
import CenteredLayout from '../../layout/CenteredLayout';
import MainLayout from '../../layout/MainLayout';
import ResponsiveView from '../../layout/ResponsiveView';
import PrimaryButton from '../../components/PrimaryButton';
import SecondaryButton from '../../components/SecondaryButton';
import { toast } from 'react-toastify';

const Container = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 40px 20px;
  text-align: center;
  max-width: 500px;
  margin: 0 auto;
`;

const IconContainer = styled.div`
  width: 80px;
  height: 80px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 40px;
  margin-bottom: 24px;
  background: #fee;
  color: #ef4444;
`;

const Title = styled.h1`
  font-size: 24px;
  font-weight: 700;
  margin-bottom: 12px;
  color: #ef4444;
`;

const Message = styled.p`
  font-size: 16px;
  color: #666;
  margin-bottom: 20px;
  line-height: 1.5;
`;

const ErrorDetailSection = styled.div`
  width: 100%;
  background: #fef2f2;
  border: 1px solid #fecaca;
  border-radius: 12px;
  padding: 20px;
  margin-bottom: 32px;
  text-align: left;
`;

const ErrorCode = styled.div`
  font-size: 14px;
  color: #991b1b;
  font-weight: 600;
  margin-bottom: 8px;
`;

const ErrorMessage = styled.div`
  font-size: 14px;
  color: #7f1d1d;
  line-height: 1.4;
`;

const DetailSection = styled.div`
  width: 100%;
  background: #f9f9f9;
  border-radius: 12px;
  padding: 20px;
  margin-bottom: 32px;
  text-align: left;
`;

const DetailRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 0;
  
  &:not(:last-child) {
    border-bottom: 1px solid #eee;
  }
`;

const DetailLabel = styled.span`
  font-size: 14px;
  color: #666;
  font-weight: 500;
`;

const DetailValue = styled.span`
  font-size: 14px;
  color: #000;
  font-weight: 600;
`;

const ButtonContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  width: 100%;
`;

interface FailureInfo {
  code: string;
  message: string;
  orderId?: string;
  amount?: string;
}

// 토스 페이먼츠 오류 코드 매핑
const ERROR_MESSAGES: Record<string, string> = {
  'PAY_PROCESS_CANCELED': '사용자가 결제를 취소했습니다.',
  'PAY_PROCESS_ABORTED': '결제 진행 중 오류가 발생했습니다.',
  'REJECT_CARD_COMPANY': '카드사에서 결제를 거절했습니다.',
  'INSUFFICIENT_FUNDS': '잔액이 부족합니다.',
  'INVALID_CARD_EXPIRATION': '카드 유효기간을 확인해주세요.',
  'INVALID_STOPPED_CARD': '정지된 카드입니다.',
  'EXCEED_MAX_DAILY_PAYMENT_COUNT': '일일 결제 한도를 초과했습니다.',
  'NOT_SUPPORTED_INSTALLMENT_PLAN': '지원되지 않는 할부 개월수입니다.',
  'INVALID_CARD_INSTALLMENT_PLAN': '유효하지 않은 할부 개월수입니다.',
  'NOT_SUPPORTED_MONTHLY_INSTALLMENT_PLAN': '월 할부를 지원하지 않는 카드입니다.',
  'EXCEED_MAX_PAYMENT_AMOUNT': '결제 가능 금액을 초과했습니다.',
  'NOT_FOUND_TERMINAL_ID': '단말기 정보를 찾을 수 없습니다.',
  'INVALID_AUTHORIZE_AUTH': '유효하지 않은 인증입니다.',
  'INVALID_CARD_LOST_STOLEN': '분실 신고된 카드입니다.',
  'RESTRICTED_TRANSFER_ACCOUNT': '계좌 이체가 제한된 계좌입니다.',
  'INVALID_CARD_NUMBER': '카드번호를 확인해주세요.',
  'INVALID_UNREGISTERED_SUBMALL': '등록되지 않은 서브몰입니다.',
  'NOT_REGISTERED_BUSINESS': '등록되지 않은 사업자입니다.',
  'EXCEED_MAX_ONE_DAY_WITHDRAW_AMOUNT': '1일 출금 한도를 초과했습니다.',
  'EXCEED_MAX_ONE_TIME_WITHDRAW_AMOUNT': '1회 출금 한도를 초과했습니다.',
  'CARD_PROCESSING_ERROR': '카드사 처리 중 오류가 발생했습니다.',
  'CARD_EXEC_ERROR': '카드 실행 오류가 발생했습니다.',
  'SYSTEM_ERROR': '일시적인 오류가 발생했습니다. 잠시 후 다시 시도해주세요.'
};

const PaymentFail: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [failureInfo, setFailureInfo] = useState<FailureInfo | null>(null);

  useEffect(() => {
    const code = searchParams.get('code');
    const message = searchParams.get('message');
    const orderId = searchParams.get('orderId');
    const amount = searchParams.get('amount');

    devLog('결제 실패 정보:', { code, message, orderId, amount });

    if (code) {
      const friendlyMessage = ERROR_MESSAGES[code] || message || '알 수 없는 오류가 발생했습니다.';
      
      setFailureInfo({
        code,
        message: friendlyMessage,
        orderId: orderId || undefined,
        amount: amount || undefined
      });

      toast.error(friendlyMessage);
    } else {
      setFailureInfo({
        code: 'UNKNOWN_ERROR',
        message: '결제 정보를 확인할 수 없습니다.',
        orderId: orderId || undefined,
        amount: amount || undefined
      });
    }
  }, [searchParams]);

  const handleRetryPayment = () => {
    if (failureInfo?.orderId) {
      // 동일한 주문으로 다시 결제 시도
      navigate(`/shopping-request/${failureInfo.orderId.replace('ORDER_', '')}`);
    } else {
      navigate('/');
    }
  };

  const handleGoHome = () => {
    navigate('/');
  };

  const handleContactSupport = () => {
    // 고객지원 페이지나 채팅으로 이동
    toast.info('고객지원 기능은 준비 중입니다.');
  };

  if (!failureInfo) {
    const NoInfoView = (
      <MainLayout>
        <CenteredLayout>
          <Container>
            <IconContainer>❌</IconContainer>
            <Title>결제 정보 오류</Title>
            <Message>결제 정보를 확인할 수 없습니다.</Message>
            <ButtonContainer>
              <PrimaryButton onClick={handleGoHome} >
                홈으로 돌아가기
              </PrimaryButton>
            </ButtonContainer>
          </Container>
        </CenteredLayout>
      </MainLayout>
    );
    
    return (
      <ResponsiveView
        mobileView={NoInfoView}
        desktopView={NoInfoView}
      />
    );
  }

  const FailView = (
    <MainLayout>
      <CenteredLayout>
        <Container>
          <IconContainer>❌</IconContainer>
          <Title>결제 실패</Title>
          <Message>
            결제 처리 중 문제가 발생했습니다.
          </Message>

          <ErrorDetailSection>
            <ErrorCode>오류 코드: {failureInfo.code}</ErrorCode>
            <ErrorMessage>{failureInfo.message}</ErrorMessage>
          </ErrorDetailSection>

          {(failureInfo.orderId || failureInfo.amount) && (
            <DetailSection>
              {failureInfo.orderId && (
                <DetailRow>
                  <DetailLabel>주문번호</DetailLabel>
                  <DetailValue>{failureInfo.orderId}</DetailValue>
                </DetailRow>
              )}
              {failureInfo.amount && (
                <DetailRow>
                  <DetailLabel>결제 시도 금액</DetailLabel>
                  <DetailValue>{Number(failureInfo.amount).toLocaleString()}원</DetailValue>
                </DetailRow>
              )}
            </DetailSection>
          )}

          <ButtonContainer>
            {failureInfo.code === 'PAY_PROCESS_CANCELED' ? (
              <PrimaryButton onClick={handleRetryPayment} >
                다시 결제하기
              </PrimaryButton>
            ) : failureInfo.code.includes('CARD') || failureInfo.code.includes('INSUFFICIENT') ? (
              <>
                <PrimaryButton onClick={handleRetryPayment} >
                  다른 결제수단으로 시도
                </PrimaryButton>
                <SecondaryButton onClick={handleContactSupport} >
                  고객지원 문의
                </SecondaryButton>
              </>
            ) : (
              <>
                <PrimaryButton onClick={handleRetryPayment} >
                  다시 시도하기
                </PrimaryButton>
                <SecondaryButton onClick={handleContactSupport} fullWidth>
                  고객지원 문의
                </SecondaryButton>
              </>
            )}
            <SecondaryButton onClick={handleGoHome} fullWidth>
              홈으로 돌아가기
            </SecondaryButton>
          </ButtonContainer>
        </Container>
      </CenteredLayout>
    </MainLayout>
  );

  return (
    <ResponsiveView
      mobileView={FailView}
      desktopView={FailView}
    />
  );
};

export default PaymentFail;